import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, MinusCircle, ChevronDown, ChevronUp } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_LABELS = {
  new:'New', test_pending:'Test Pending', test_completed:'Completed',
  shortlisted:'Shortlisted', interview_scheduled:'Interview Scheduled',
  rejected:'Rejected', on_hold:'On Hold',
};

const CAT_COLOR = { english:'#1e73be', maths:'#2ea690', logical:'#7c3aed', reading:'#ea580c' };
const LABEL_STYLE = {
  'Excellent':         { bg:'#f0fdf4', text:'#16a34a' },
  'Very Good':         { bg:'#eff6ff', text:'#1d4ed8' },
  'Good':              { bg:'#fefce8', text:'#ca8a04' },
  'Average':           { bg:'#fff7ed', text:'#ea580c' },
  'Needs Improvement': { bg:'#fef2f2', text:'#dc2626' },
};

export default function AdminAssessmentCandidateDetail() {
  const { id }        = useParams();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [expCat,  setExpCat]  = useState(null);
  const [updStatus, setUpdStatus] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/admin/assessment/candidates/${id}`)
      .then(r => {
        setData(r.data);
        setUpdStatus(r.data.candidate?.status || '');
      })
      .catch(() => setError('Could not load candidate details.'))
      .finally(() => setLoading(false));
  }, [id]);

  const saveStatus = async () => {
    try {
      await axios.patch(`${API}/api/admin/assessment/candidates/${id}/status`, { status: updStatus });
    } catch { /* ignore */ }
  };

  if (loading) return <div className="py-12 text-center text-slate-500">Loading…</div>;
  if (error)   return <div className="py-12 text-center text-red-500">{error}</div>;

  const { candidate: c, attempt: a, responses, tab_switch_count } = data;
  const score = a?.status === 'completed' ? a : null;

  const sections = ['english','maths','logical','reading'];
  const bySection = {};
  sections.forEach(s => {
    bySection[s] = responses.filter(r => r.category === s);
  });

  const ls = LABEL_STYLE[a?.performance_label] || LABEL_STYLE['Average'];
  const timeTaken = a?.time_taken_seconds;
  const timeFmt = timeTaken ? `${Math.floor(timeTaken/60)}m ${timeTaken%60}s` : '—';

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/portal/admin/assessment/candidates"
          className="text-sm text-[#1e73be] hover:underline">← Candidates</Link>
        <span className="text-slate-300">/</span>
        <span className="text-sm text-slate-600">{c.full_name}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Candidate Info */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="font-semibold text-slate-800 mb-4">Candidate Info</h2>
          <div className="space-y-3 text-sm">
            {[
              ['Name',               c.full_name],
              ['Phone',              c.phone],
              ['Email',              c.email || '—'],
              ['Marital Status',     c.marital_status || '—'],
              ['Department',         c.department || '—'],
              ['Applied For',        c.designation_applied || '—'],
              ['Current/Last Role',  c.job_role || '—'],
              ['Last Company',       c.last_company || '—'],
              ['Total Experience',   c.total_experience || '—'],
              ['Qualification',       c.highest_qualification || '—'],
              ['Test Center',        c.test_center || '—'],
              ['Currently Employed', c.currently_employed === 'yes' ? '✅ Yes' : c.currently_employed === 'no' ? '❌ No' : '—'],
              ['Notice Period',      c.notice_period || '—'],
              ['Immediate Joiner',   c.immediate_joiner === 'yes' ? '🚀 Yes' : c.immediate_joiner === 'no' ? '📅 No' : '—'],
              ['Current Salary',     c.current_salary ? `₹${c.current_salary}/mo` : '—'],
              ['Expected Salary',    c.expected_salary ? `₹${c.expected_salary}/mo` : '—'],
              ['Native Place',       c.native_place || '—'],
            ].map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <span className="text-slate-400 w-32 shrink-0">{k}:</span>
                <span className="text-slate-700 font-medium">{v}</span>
              </div>
            ))}
            {c.reason_for_leaving && (
              <div className="pt-2 border-t border-slate-100">
                <p className="text-slate-400 text-xs mb-1">Reason for Leaving:</p>
                <p className="text-slate-700 text-sm">{c.reason_for_leaving}</p>
              </div>
            )}
            {c.employment_gap && (
              <div className="pt-2 border-t border-slate-100">
                <p className="text-slate-400 text-xs mb-1">Employment Gap:</p>
                <p className="text-slate-700 text-sm">{c.employment_gap}</p>
              </div>
            )}
            {c.address && (
              <div className="flex gap-2 pt-2 border-t border-slate-100">
                <span className="text-slate-400 w-32 shrink-0">Address:</span>
                <span className="text-slate-700 text-sm">{c.address}</span>
              </div>
            )}
          </div>

          <div className="mt-5 border-t pt-4">
            <p className="text-xs text-slate-500 mb-2">Update Status</p>
            <div className="flex gap-2">
              <select value={updStatus} onChange={e => setUpdStatus(e.target.value)}
                className="flex-1 text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white outline-none">
                {Object.entries(STATUS_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <button onClick={saveStatus}
                className="px-3 py-2 rounded-lg text-white text-sm font-medium hover:brightness-110 transition"
                style={{ backgroundColor: '#1e73be' }}>Save</button>
            </div>
          </div>
        </div>

        {/* Score */}
        <div className="lg:col-span-2 space-y-4">
          {score ? (
            <>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-slate-800">Test Results</h2>
                  <span className="text-xs px-3 py-1 rounded-full font-semibold" style={{ backgroundColor: ls.bg, color: ls.text }}>
                    {a.performance_label}
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  {[
                    { label:'Overall', value: a.overall_pct?.toFixed(1)+'%', big: true },
                    { label:'Time',    value: timeFmt },
                    { label:'Correct', value: a.correct_count },
                    { label:'Wrong',   value: a.wrong_count },
                  ].map(({ label, value, big }) => (
                    <div key={label} className="bg-slate-50 rounded-xl p-3 text-center">
                      <p className={`font-bold text-slate-800 ${big ? 'text-3xl' : 'text-xl'}`}>{value}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{label}</p>
                    </div>
                  ))}
                </div>

                {/* Tab-switch violations */}
                {tab_switch_count > 0 && (
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-4 text-sm font-medium ${
                    tab_switch_count >= 3
                      ? 'bg-red-50 border border-red-200 text-red-700'
                      : 'bg-amber-50 border border-amber-200 text-amber-700'
                  }`}>
                    <span className="text-base">⚠️</span>
                    <span>
                      Tab-switch violations: <strong>{tab_switch_count}</strong>
                      {tab_switch_count >= 3 && ' — Test was auto-submitted'}
                    </span>
                  </div>
                )}

                {/* Section bars */}
                <div className="space-y-3">
                  {[
                    { key:'english_pct',  label:'English',   score: a.english_score },
                    { key:'maths_pct',    label:'Maths',     score: a.maths_score   },
                    { key:'logical_pct',  label:'Logical',   score: a.logical_score },
                    { key:'reading_pct',  label:'Reading',   score: a.reading_score },
                  ].map(({ key, label, score: sc }) => {
                    const pct = a[key] || 0;
                    const col = CAT_COLOR[label.toLowerCase()] || '#1e73be';
                    return (
                      <div key={key}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-600">{label}</span>
                          <span className="font-semibold" style={{ color: col }}>{pct.toFixed(1)}%</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width:`${pct}%`, backgroundColor: col }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Responses by section */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-800 mb-4">Question-wise Responses</h3>
                <div className="space-y-3">
                  {sections.filter(s => bySection[s].length > 0).map(cat => (
                    <div key={cat} className="border border-slate-100 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpCat(expCat === cat ? null : cat)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50">
                        <span className="font-medium text-slate-700 capitalize"
                          style={{ color: CAT_COLOR[cat] }}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)} ({bySection[cat].length} Qs)
                        </span>
                        {expCat === cat ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </button>
                      {expCat === cat && (
                        <div className="border-t border-slate-100 divide-y divide-slate-50">
                          {bySection[cat].map((r, i) => (
                            <div key={i} className="px-4 py-3 text-sm">
                              <p className="text-slate-700 font-medium mb-1">{i+1}. {r.question_text}</p>
                              <div className="flex items-center gap-4 text-xs mt-1">
                                <span>Candidate: <strong className={r.is_correct ? 'text-green-600' : r.is_correct === false ? 'text-red-500' : 'text-slate-400'}>
                                  {r.selected_answer ? `Option ${r.selected_answer}` : 'Not attempted'}
                                </strong></span>
                                <span>Correct: <strong className="text-green-700">Option {r.correct_answer}</strong></span>
                                <span className="flex items-center gap-1">
                                  {r.is_correct === true  ? <><CheckCircle className="w-3.5 h-3.5 text-green-500" /> +{r.marks_awarded}</> :
                                   r.is_correct === false ? <><XCircle     className="w-3.5 h-3.5 text-red-500"   /> {r.marks_awarded}</> :
                                                            <><MinusCircle  className="w-3.5 h-3.5 text-slate-300" /> 0</>}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <p className="text-slate-500">
                {a ? `Test status: ${a.status}` : 'No test attempt found for this candidate.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
